import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Input } from '@app/form';
import {
  ProductPriceUnit,
  ProductType,
  useAddProductToSessionOrders,
  useCreateProduct,
} from 'queries';
import {
  ProductAutocomple,
  ProductOption,
} from 'components/AdminPriceEdition/ProductAutocomplete';
import { useToast, SquareButton, useBoolModalState } from 'components';
import { toEuros } from 'i18n';
import { snakeCase } from 'lodash';
import { ChangeEvent } from 'preact/compat';

const useStyles = createUseStyles((theme: Theme) => ({
  line: {
    ...theme.fonts.caption,
    color: theme.colors.inkPrimary,
  },
  title: {
    ...theme.fonts.label,
  },
  price: {
    ...theme.fonts.caption,
    textAlign: 'center',
    width: 120,
  },
  addButtonLine: {
    position: 'relative',
    '& td.no-height-line': {
      height: 0,
      padding: 0,
      borderBottom: {
        color: theme.colors.green,
        style: 'dashed',
        width: 1,
      },
    },
  },
  addButton: {
    position: 'absolute',
    top: -theme.marginBase * 2,
    left: '50%',
    right: '50%',
    border: {
      color: theme.colors.green,
      style: 'solid',
      width: 1,
    },
  },
  lastCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
}));

interface Props {
  sessionId?: string;
}

interface Values {
  option?: ProductOption;
  price?: number | null;
  isNew?: boolean;
}

export const AddBillingTableProductLine = ({ sessionId }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { mutateAsync: createProduct } = useCreateProduct();
  const [values, setValues] = useState<Values>({});

  const [adding, startAdding, stopAdding] = useBoolModalState(false);

  const { mutateAsync: addProduct } = useAddProductToSessionOrders(sessionId);

  const onProductChange = useCallback(
    (data: ProductOption) => {
      const details = data.details;
      if (details) {
        setValues({
          option: data,
          price: details.price,
          isNew: false,
        });
      } else {
        setValues({ option: data, price: 0, isNew: true });
      }
    },
    [setValues],
  );

  const onPriceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = (event.currentTarget || event.target).value;
      setValues((curr) => ({
        ...curr,
        price: value !== '' ? Number(value) * 100 || 0 : null,
      }));
    },
    [setValues],
  );

  const save = useCallback(async () => {
    try {
      if (!values.option) {
        return;
      }

      let productId = values.option.value;
      if (values.isNew) {
        const title = values.option.value;
        const rand = Math.floor(Math.random() * 1000);
        const name = snakeCase(title).toUpperCase() + '_' + rand;
        const price = values.price || 0;

        const product = await createProduct({
          name: 'GENERATED_' + name,
          internalName: title,
          title,
          type: ProductType.FEATURE,
          pricePerParticipant: false,
          priceUnit: ProductPriceUnit.EUR,
          generated: true,
          active: false,
          price: Math.floor(price),
        });
        productId = product.id;
      }

      await addProduct({ id: productId });
      stopAdding();
      setValues({});
    } catch (e) {
      toast.exception(e);
    }
  }, [createProduct, toast, values, stopAdding, addProduct]);

  if (!adding) {
    return (
      <tr className={classes.addButtonLine}>
        <td colSpan={5} className="no-height-line">
          <SquareButton
            icon="ri-add-line"
            color="green"
            small
            className={classes.addButton}
            onClick={startAdding}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr className={classes.line}>
      <td className={classes.title}>
        <ProductAutocomple
          productType={ProductType.FEATURE}
          onChange={onProductChange}
          value={values.option}
        />
      </td>
      <td className={classes.price}>
        <Input
          type="number"
          disabled={!values.isNew}
          noMargin
          value={values.price || values.price === 0 ? values.price / 100 : null}
          onChange={onPriceChange}
        />
      </td>
      <td className={classes.price}>
        <Input type="number" noMargin value={1} disabled />
      </td>
      <td className={classes.price}>{toEuros(values.price || 0)}</td>
      <td>
        <SquareButton
          icon="ri-check-line"
          color="green"
          small
          variant="plain"
          onClick={save}
        />
      </td>
    </tr>
  );
};
